import type { WebConversationListItem } from '../../lib/chat-api';

export function formatRelativeTime(timestamp: number): string {
  const deltaSeconds = Math.max(0, Math.floor(Date.now() / 1000 - timestamp));
  if (deltaSeconds < 10) return 'now';
  if (deltaSeconds < 60) return `${deltaSeconds}s`;
  if (deltaSeconds < 3600) return `${Math.floor(deltaSeconds / 60)}m`;
  if (deltaSeconds < 86400) return `${Math.floor(deltaSeconds / 3600)}h`;
  return `${Math.floor(deltaSeconds / 86400)}d`;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remain = seconds % 60;
  return remain === 0 ? `${minutes}m` : `${minutes}m ${remain}s`;
}

export type ConversationDateGroup = {
  id: string;
  label: string;
  conversations: WebConversationListItem[];
};

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number) {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

function getConversationDateBucket(
  timestamp: number,
  boundaries: {
    today: number;
    yesterday: number;
    previousSevenDays: number;
    previousThirtyDays: number;
  },
  olderFormatter: Intl.DateTimeFormat
) {
  const timestampMs = timestamp * 1000;
  if (timestampMs >= boundaries.today) {
    return { id: 'today', label: 'Today' };
  }

  if (timestampMs >= boundaries.yesterday) {
    return { id: 'yesterday', label: 'Yesterday' };
  }

  if (timestampMs >= boundaries.previousSevenDays) {
    return { id: 'previous-7-days', label: 'Previous 7 Days' };
  }

  if (timestampMs >= boundaries.previousThirtyDays) {
    return { id: 'previous-30-days', label: 'Previous 30 Days' };
  }

  const date = new Date(timestampMs);
  const monthId = `${date.getFullYear()}-${date.getMonth()}`;
  return {
    id: `month-${monthId}`,
    label: olderFormatter.format(date),
  };
}

export function groupConversationsByDate(
  conversations: WebConversationListItem[],
  now = new Date()
): ConversationDateGroup[] {
  const startOfToday = startOfDay(now);
  const boundaries = {
    today: startOfToday.getTime(),
    yesterday: addDays(startOfToday, -1).getTime(),
    previousSevenDays: addDays(startOfToday, -7).getTime(),
    previousThirtyDays: addDays(startOfToday, -30).getTime(),
  };
  const olderFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'long',
    year: 'numeric',
  });
  const groupedConversations: ConversationDateGroup[] = [];

  for (const conversation of conversations) {
    const bucket = getConversationDateBucket(
      conversation.updatedAt || conversation.createdAt,
      boundaries,
      olderFormatter
    );
    const existingGroup = groupedConversations.find(
      group => group.id === bucket.id
    );

    if (existingGroup) {
      existingGroup.conversations.push(conversation);
      continue;
    }

    groupedConversations.push({
      id: bucket.id,
      label: bucket.label,
      conversations: [conversation],
    });
  }

  return groupedConversations;
}
